/**
 * ============================================================================
 * 🔐 useServerAuth — Auth-Hook für eigenen Server
 * ============================================================================
 * 
 * Google Sign-In → Server Login → JWT
 * 
 * Features:
 * - Kein Firebase Auth SDK für Daten nötig
 * - Google Sign-In → Server Login → JWT statt Firebase Token
 * - Refresh Token (30 Tage) statt Firebase Session (1 Std)
 * - Drive Token vom Server (kein Re-Auth nötig!)
 * - Session-Expired Event für automatischen Re-Login
 * 
 * API:
 * - currentUser, authLoading, authError
 * - handleGoogleSignIn, handleLogout
 * ============================================================================
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { signInWithGoogle as gisSignIn, requestDriveToken, revokeGoogleSession } from './googleAuthService';
import { 
  loginWithGoogle, serverLogout, getStoredUser, isLoggedIn, 
  clearTokens, getDriveToken, storeDriveToken 
} from './apiService';
import { setGoogleAccessToken, clearGoogleAccessToken } from './googleAuthToken';
import { set as idbSet, get as idbGet } from 'idb-keyval';

/**
 * Server-basierter Auth-Hook
 * 
 * @param {Function} [onAuthStateChange] - Callback wenn Auth-State sich ändert
 * @param {Function} [onLoginSuccess] - Callback bei erfolgreichem Login
 * @returns {Object} { currentUser, authLoading, authError, handleGoogleSignIn, handleLogout }
 */
function useServerAuth(onAuthStateChange, onLoginSuccess) {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // Anmeldung liegt am Gerät, aber der Server war beim Erneuern nicht erreichbar → es wird weiter versucht
  const [reconnecting, setReconnectingState] = useState(false);
  const reconnectingRef = useRef(false);
  const setReconnecting = (v) => { reconnectingRef.current = v; setReconnectingState(v); };
  const angemeldetRef = useRef(false);   // Sitzung steht (wiederhergestellt oder frisch angemeldet)
  const retryRef = useRef(() => {});     // „Jetzt erneut versuchen"

  /**
   * Google Sign-In → Server Login
   * 1. Natives Google Sign-In (liefert idToken + accessToken)
   * 2. Sende an unseren Server → bekomme JWT
   * 3. Drive Token wird auf dem Server gecacht
   */
  const handleGoogleSignIn = useCallback(async (e) => {
    if (e) e.preventDefault();
    setAuthError(null);

    try {
      console.log('🔐 [Server] Starte Google-Login via GIS...');

      // 1. Google Sign-In via Google Identity Services (GIS)
      const result = await gisSignIn();

      if (!result.idToken) {
        throw new Error('Kein Google ID Token erhalten');
      }

      console.log('✅ [Server] Google Sign-In erfolgreich, sende an Server...');

      // 2. An unseren Server senden → JWT erhalten
      const serverResult = await loginWithGoogle(
        result.idToken,
        null, // Access Token wird separat angefragt
        null, // Kein serverAuthCode bei GIS Web-Flow
        navigator.userAgent
      );

      // 3. User-Objekt aufbauen
      const user = {
        uid: serverResult.user.googleId,
        email: serverResult.user.email,
        displayName: serverResult.user.name,
        photoURL: serverResult.user.picture,
        serverId: serverResult.user.id,
        getIdToken: async () => serverResult.accessToken,
      };

      // 4. Drive Token im Hintergrund anfordern (GIS Token Client)
      requestDriveToken(false).then(driveToken => {
        if (driveToken) {
          setGoogleAccessToken(driveToken);
          console.log('🔑 [Server] Drive Token via GIS erhalten ✅');
          // Auch auf dem Server cachen für spätere Requests
          storeDriveToken(driveToken).catch(() => {});
        }
      }).catch(() => {
        console.log('🔑 [Server] Drive Token wird beim ersten Zugriff geholt (Popup blockiert — normal bei Web)');
      });

      // 5. Status setzen
      angemeldetRef.current = true;
      setReconnecting(false);
      setCurrentUser(user);
      localStorage.setItem('wasEverAuthenticated', 'true');
      idbSet('lastAuthenticatedAt', Date.now()).catch(() => {});

      console.log('✅ [Server] Login komplett:', user.email);

      // Event für andere Komponenten (z.B. Admin-Check in App.js)
      window.dispatchEvent(new CustomEvent('kiosk:auth-changed', { detail: { user } }));

      // Callbacks
      if (onAuthStateChange) onAuthStateChange(user);
      if (onLoginSuccess) setTimeout(() => onLoginSuccess(user), 500);

      return user;

    } catch (error) {
      console.error('❌ [Server] Login fehlgeschlagen:', error);
      setAuthError(error.message);
      throw error;
    }
  }, [onAuthStateChange, onLoginSuccess]);

  /**
   * Logout
   */
  const handleLogout = useCallback(async () => {
    try {
      await serverLogout();
      revokeGoogleSession();
      clearGoogleAccessToken();

      angemeldetRef.current = false;
      setReconnecting(false);
      setCurrentUser(null);
      localStorage.removeItem('wasEverAuthenticated');
      // Bei explizitem Logout: lastAuthenticatedAt NICHT löschen (Timestamp bleibt für 76h-Check)

      console.log('✅ [Server] Logout erfolgreich');

      if (onAuthStateChange) onAuthStateChange(null);
    } catch (error) {
      console.error('❌ [Server] Logout-Fehler:', error);
      setAuthError(error.message);
    }
  }, [onAuthStateChange]);

  /**
   * Initialisierung: Prüfe gespeicherte Session
   */
  useEffect(() => {
    // 🔁 Erneuerung vorübergehend gescheitert (Server-Störung, WLAN beim Aufwachen noch nicht da):
    // Die Anmeldung liegt weiter am Gerät. Früher blieb die App dann bis zum Neustart auf dem
    // Login-Bildschirm stehen — jetzt versucht sie es im Hintergrund weiter und stellt die Sitzung
    // von selbst wieder her (gefunden 01.10.2026: /auth/refresh antwortete beim Ausfall mit 500).
    let beendet = false;
    let retryTimer = null;
    let versuch = 0;
    const erneutVersuchen = async () => {
      if (beendet || angemeldetRef.current) { setReconnecting(false); return; }
      clearTimeout(retryTimer);
      const { getRefreshToken, refreshAccessToken } = await import('./apiService');
      if (!getRefreshToken()) { setReconnecting(false); return; } // inzwischen abgemeldet / endgültig abgelehnt
      try {
        await refreshAccessToken();
        if (beendet || angemeldetRef.current) return;
        console.log('✅ [Server] Verbindung wieder da — Sitzung wird wiederhergestellt');
        setReconnecting(false);
        stelleSitzungWiederHer();
      } catch (e) {
        if (beendet) return;
        if (!getRefreshToken()) { setReconnecting(false); return; } // 401/403 → echter Login nötig
        versuch += 1;
        retryTimer = setTimeout(erneutVersuchen, Math.min(30000, 3000 * 2 ** Math.min(versuch, 4))); // 6 s, 12 s, 24 s, dann alle 30 s
      }
    };
    retryRef.current = () => { versuch = 0; erneutVersuchen(); };
    const beiRueckkehr = () => { if (reconnectingRef.current && document.visibilityState !== 'hidden') retryRef.current(); };
    window.addEventListener('online', beiRueckkehr);
    document.addEventListener('visibilitychange', beiRueckkehr);

    const stelleSitzungWiederHer = () => {
          // Gespeicherte Session wiederherstellen
          const storedUser = getStoredUser();
          if (storedUser) {
            const user = {
              uid: storedUser.googleId,
              email: storedUser.email,
              displayName: storedUser.name,
              photoURL: storedUser.picture,
              serverId: storedUser.id,
              getIdToken: async () => {
                // Lazy: Bei Bedarf importieren
                const { getAccessToken } = await import('./apiService');
                return getAccessToken();
              },
            };

            angemeldetRef.current = true;
            setCurrentUser(user);
            idbSet('lastAuthenticatedAt', Date.now()).catch(() => {});
            console.log('🔐 [Server] Session wiederhergestellt:', user.email);

            // Event für andere Komponenten (z.B. Admin-Check)
            window.dispatchEvent(new CustomEvent('kiosk:auth-changed', { detail: { user } }));

            if (onAuthStateChange) onAuthStateChange(user);
            if (onLoginSuccess) setTimeout(() => onLoginSuccess(user), 500);

            // Drive Token vom Server holen — mit Verzögerung damit JWT-Refresh zuerst abgeschlossen ist
            setTimeout(() => {
              getDriveToken()
                .then(token => {
                  if (token) {
                    setGoogleAccessToken(token);
                    console.log('🔑 [Server] Drive Token vom Server geholt ✅');
                  }
                })
                .catch(() => {
                  console.log('🔑 [Server] Kein Drive Token — wird lazy geholt');
                });
            }, 2000);
          }
    };

    const initAuth = async () => {
      try {
        // Prüfe ob Access Token noch gültig ist
        let loggedIn = isLoggedIn();

        // Access Token abgelaufen, aber Refresh Token vorhanden? → Token erneuern
        if (!loggedIn) {
          const { getRefreshToken, refreshAccessToken } = await import('./apiService');
          const refreshToken = getRefreshToken();
          if (refreshToken) {
            console.log('🔄 [Server] Access Token abgelaufen, versuche Refresh...');
            try {
              await refreshAccessToken();
              loggedIn = true;
              console.log('✅ [Server] Token erfolgreich erneuert');
            } catch (refreshErr) {
              if (getRefreshToken() && getStoredUser()) {
                // Nur vorübergehend (Server/Netz): Anmeldung bleibt, im Hintergrund weiter versuchen
                console.log('⏳ [Server] Refresh vorübergehend fehlgeschlagen — versuche es weiter');
                setReconnecting(true);
                retryTimer = setTimeout(erneutVersuchen, 3000);
              } else {
                console.log('❌ [Server] Refresh fehlgeschlagen — erneuter Login nötig');
              }
            }
          }
        }

        if (loggedIn) {
          stelleSitzungWiederHer();
        } else {
          console.log('🔐 [Server] Keine gespeicherte Session');
        }
      } catch (err) {
        console.error('❌ [Server] Init-Fehler:', err);
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();

    // 🔄 Drive Token Auto-Refresh alle 50 Minuten (Token läuft nach 60 Min ab)
    const DRIVE_REFRESH_INTERVAL = 50 * 60 * 1000; // 50 Minuten
    const driveRefreshTimer = setInterval(() => {
      if (!isLoggedIn()) return; // Nicht eingeloggt → nichts tun
      console.log('🔄 [Server] Drive Token Auto-Refresh...');
      getDriveToken()
        .then(token => {
          if (token) {
            setGoogleAccessToken(token);
            console.log('🔑 [Server] Drive Token auto-refreshed ✅');
          }
        })
        .catch(err => {
          console.warn('🔑 [Server] Drive Token Auto-Refresh fehlgeschlagen:', err.message);
        });
    }, DRIVE_REFRESH_INTERVAL);

    // Session-Expired Event Listener
    const handleSessionExpired = () => {
      console.log('🚨 [Server] Session abgelaufen!');
      angemeldetRef.current = false;
      setCurrentUser(null);
      clearGoogleAccessToken();

      if (onAuthStateChange) onAuthStateChange(null);

      // Alert wie bei Firebase-Logout
      setTimeout(() => {
        alert(
          '🚨 WICHTIG: Deine Session ist abgelaufen!\n\n' +
          'Bitte logge dich neu ein.'
        );
        window.location.reload();
      }, 500);
    };

    window.addEventListener('kiosk:session-expired', handleSessionExpired);

    return () => {
      beendet = true;
      clearTimeout(retryTimer);
      window.removeEventListener('online', beiRueckkehr);
      document.removeEventListener('visibilitychange', beiRueckkehr);
      clearInterval(driveRefreshTimer);
      window.removeEventListener('kiosk:session-expired', handleSessionExpired);
    };
  }, []); // Nur einmal beim Mount

  return {
    currentUser,
    authLoading,
    authError,
    authReconnecting: reconnecting && !currentUser,
    retryConnection: () => retryRef.current(),
    handleGoogleSignIn,
    handleLogout,
    isAuthenticated: () => currentUser !== null,
    getUserInfo: () => currentUser ? {
      uid: currentUser.uid,
      email: currentUser.email,
      displayName: currentUser.displayName,
      photoURL: currentUser.photoURL,
    } : null,
  };
}

export default useServerAuth;
